/**
 * # DevFeatures - Configurazione Sviluppo
 *
 * Sistema centralizzato per toggle funzionalità di sviluppo.
 * Permette switch rapido tra mock data e dati reali.
 *
 * ## Priorità configurazione (dalla più alta):
 * 1. localStorage (runtime, persistente)
 * 2. Environment variable (build time)
 * 3. Default hardcoded
 *
 * ## Uso da Console Browser:
 * ```js
 * // Attiva mock data
 * localStorage.setItem('DEV_USE_MOCK_DATA', 'true');
 * location.reload();
 *
 * // Disattiva mock data (usa API reali)
 * localStorage.setItem('DEV_USE_MOCK_DATA', 'false');
 * location.reload();
 *
 * // Reset a default
 * localStorage.removeItem('DEV_USE_MOCK_DATA');
 * location.reload();
 * ```
 */

// ============================================================
// FEATURE FLAGS
// ============================================================

export interface DevFeatureFlags {
  /** Usa mock data invece di chiamate API reali */
  useMockData: boolean;
  /** Mostra indicatore mock/real nella UI */
  showDataSourceIndicator: boolean;
  /** Abilita logging dettagliato API calls */
  verboseApiLogging: boolean;
  /** Simula latenza rete per mock data (ms) */
  mockNetworkDelay: number;
  /** Abilita DevSimulator per form profilo */
  devSimulatorEnabled: boolean;
}

// ============================================================
// DEFAULTS
// ============================================================

const DEFAULTS: DevFeatureFlags = {
  useMockData: false, // Default: LIVE - dati reali da API
  showDataSourceIndicator: true, // Mostra badge "MOCK" o "LIVE"
  verboseApiLogging: false,
  mockNetworkDelay: 300, // Simula 300ms latenza
  devSimulatorEnabled: true,
};

// ============================================================
// STORAGE KEYS
// ============================================================

const STORAGE_KEYS = {
  useMockData: 'DEV_USE_MOCK_DATA',
  showDataSourceIndicator: 'DEV_SHOW_DATA_SOURCE',
  verboseApiLogging: 'DEV_VERBOSE_API',
  mockNetworkDelay: 'DEV_MOCK_DELAY',
  devSimulatorEnabled: 'DEV_SIMULATOR_ENABLED',
} as const;

// ============================================================
// HELPER FUNCTIONS
// ============================================================

/**
 * Legge valore da localStorage con fallback
 */
function getStorageValue<T>(key: string, defaultValue: T): T {
  if (typeof window === 'undefined') return defaultValue;

  const stored = localStorage.getItem(key);
  if (stored === null) return defaultValue;

  // Parse boolean
  if (stored === 'true') return true as T;
  if (stored === 'false') return false as T;

  // Parse number
  const num = Number(stored);
  if (!isNaN(num)) return num as T;

  // Return as string
  return stored as T;
}

/**
 * Scrive valore in localStorage
 */
function setStorageValue(key: string, value: unknown): void {
  if (typeof window === 'undefined') return;
  localStorage.setItem(key, String(value));
}

/**
 * Rimuove valore da localStorage (reset a default)
 */
function removeStorageValue(key: string): void {
  if (typeof window === 'undefined') return;
  localStorage.removeItem(key);
}

// ============================================================
// FEATURE FLAGS API
// ============================================================

/**
 * Ottiene tutte le feature flags correnti
 */
export function getDevFeatures(): DevFeatureFlags {
  return {
    useMockData: getStorageValue(STORAGE_KEYS.useMockData, DEFAULTS.useMockData),
    showDataSourceIndicator: getStorageValue(STORAGE_KEYS.showDataSourceIndicator, DEFAULTS.showDataSourceIndicator),
    verboseApiLogging: getStorageValue(STORAGE_KEYS.verboseApiLogging, DEFAULTS.verboseApiLogging),
    mockNetworkDelay: getStorageValue(STORAGE_KEYS.mockNetworkDelay, DEFAULTS.mockNetworkDelay),
    devSimulatorEnabled: getStorageValue(STORAGE_KEYS.devSimulatorEnabled, DEFAULTS.devSimulatorEnabled),
  };
}

/**
 * Imposta una feature flag
 */
export function setDevFeature<K extends keyof DevFeatureFlags>(
  key: K,
  value: DevFeatureFlags[K]
): void {
  setStorageValue(STORAGE_KEYS[key], value);

  // Log per feedback
  console.log(`[DevFeatures] ${key} = ${value}`);
}

/**
 * Reset una feature flag a default
 */
export function resetDevFeature(key: keyof DevFeatureFlags): void {
  removeStorageValue(STORAGE_KEYS[key]);
  console.log(`[DevFeatures] ${key} reset to default: ${DEFAULTS[key]}`);
}

/**
 * Reset tutte le feature flags
 */
export function resetAllDevFeatures(): void {
  Object.values(STORAGE_KEYS).forEach(removeStorageValue);
  console.log('[DevFeatures] All features reset to defaults');
}

// ============================================================
// SHORTCUT FUNCTIONS
// ============================================================

/**
 * Controlla se usare mock data
 *
 * @example
 * ```tsx
 * const data = useMockData()
 *   ? mockArticoli
 *   : await fetchArticoli();
 * ```
 */
export function useMockData(): boolean {
  return getDevFeatures().useMockData;
}

/**
 * Controlla se mostrare indicatore data source
 */
export function showDataSourceIndicator(): boolean {
  return getDevFeatures().showDataSourceIndicator;
}

/**
 * Ottiene delay simulato per mock (0 se non mock)
 */
export function getMockDelay(): number {
  const features = getDevFeatures();
  return features.useMockData ? features.mockNetworkDelay : 0;
}

/**
 * Simula latenza rete per mock data
 *
 * @example
 * ```tsx
 * const data = useMockData() ? mockData : await fetchData();
 * await simulateMockDelay();
 * return data;
 * ```
 */
export async function simulateMockDelay(): Promise<void> {
  const delay = getMockDelay();
  if (delay > 0) {
    await new Promise(resolve => setTimeout(resolve, delay));
  }
}

// ============================================================
// DATA FETCHING HELPER
// ============================================================

interface FetchWithMockOptions<T> {
  /** Dati mock da usare se mock mode attivo */
  mockData: T;
  /** Funzione per fetch dati reali */
  fetchReal: () => Promise<T>;
  /** Nome feature per logging */
  featureName?: string;
}

/**
 * Helper per fetch con fallback automatico a mock
 *
 * @example
 * ```tsx
 * const articoli = await fetchWithMock({
 *   mockData: mockArticoli,
 *   fetchReal: () => api.get('/articoli'),
 *   featureName: 'articoli'
 * });
 * ```
 */
export async function fetchWithMock<T>({
  mockData,
  fetchReal,
  featureName = 'data'
}: FetchWithMockOptions<T>): Promise<T> {
  const features = getDevFeatures();

  if (features.useMockData) {
    if (features.verboseApiLogging) {
      console.log(`[DevFeatures] Using MOCK ${featureName}`);
    }
    await simulateMockDelay();
    return mockData;
  }

  try {
    if (features.verboseApiLogging) {
      console.log(`[DevFeatures] Fetching REAL ${featureName}...`);
    }
    const result = await fetchReal();
    if (features.verboseApiLogging) {
      console.log(`[DevFeatures] REAL ${featureName} loaded`);
    }
    return result;
  } catch (error) {
    console.warn(`[DevFeatures] Failed to fetch ${featureName}, falling back to mock:`, error);
    return mockData;
  }
}

// ============================================================
// REACT HOOK
// ============================================================

import { useState, useEffect, useCallback } from 'react';

/**
 * React hook per accedere e modificare feature flags
 *
 * @example
 * ```tsx
 * const { features, setFeature, resetAll } = useDevFeatures();
 *
 * return (
 *   <Switch
 *     checked={features.useMockData}
 *     onChange={(v) => setFeature('useMockData', v)}
 *   />
 * );
 * ```
 */
export function useDevFeatures() {
  const [features, setFeatures] = useState<DevFeatureFlags>(getDevFeatures);

  // Refresh features on storage change (cross-tab sync)
  useEffect(() => {
    const handleStorage = (e: StorageEvent) => {
      if (Object.values(STORAGE_KEYS).includes(e.key as typeof STORAGE_KEYS[keyof typeof STORAGE_KEYS])) {
        setFeatures(getDevFeatures());
      }
    };

    window.addEventListener('storage', handleStorage);
    return () => window.removeEventListener('storage', handleStorage);
  }, []);

  const setFeature = useCallback(<K extends keyof DevFeatureFlags>(
    key: K,
    value: DevFeatureFlags[K]
  ) => {
    setDevFeature(key, value);
    setFeatures(prev => ({ ...prev, [key]: value }));
  }, []);

  const resetFeature = useCallback((key: keyof DevFeatureFlags) => {
    resetDevFeature(key);
    setFeatures(getDevFeatures());
  }, []);

  const resetAll = useCallback(() => {
    resetAllDevFeatures();
    setFeatures(getDevFeatures());
  }, []);

  return {
    features,
    setFeature,
    resetFeature,
    resetAll,
    // Shortcuts
    isMockMode: features.useMockData,
  };
}

// ============================================================
// CONSOLE HELPERS (for development)
// ============================================================

// Expose to window for easy console access
if (typeof window !== 'undefined') {
  (window as unknown as Record<string, unknown>).devFeatures = {
    get: getDevFeatures,
    set: setDevFeature,
    reset: resetDevFeature,
    resetAll: resetAllDevFeatures,
    useMock: () => setDevFeature('useMockData', true),
    useLive: () => setDevFeature('useMockData', false),
    help: () => {
      console.log(`
╔════════════════════════════════════════════════════════════════╗
║  DevFeatures - Console Commands                                 ║
╠════════════════════════════════════════════════════════════════╣
║  devFeatures.get()           → View current settings           ║
║  devFeatures.useMock()       → Switch to MOCK data             ║
║  devFeatures.useLive()       → Switch to LIVE/API data         ║
║  devFeatures.set(key, val)   → Set specific feature            ║
║  devFeatures.reset(key)      → Reset feature to default        ║
║  devFeatures.resetAll()      → Reset all to defaults           ║
╠════════════════════════════════════════════════════════════════╣
║  After changing, reload page: location.reload()                ║
╚════════════════════════════════════════════════════════════════╝
      `);
    }
  };

  // Auto-show help in development
  if (import.meta.env.DEV) {
    console.log('[DevFeatures] Type devFeatures.help() for commands');
  }
}

// ============================================================
// DATA SOURCE INDICATOR COMPONENT
// ============================================================

import React from 'react';

interface DataSourceBadgeProps {
  /** Override per forzare visualizzazione */
  forceShow?: boolean;
  /** Classe CSS aggiuntiva */
  className?: string;
}

/**
 * Badge che indica se i dati sono MOCK o LIVE
 *
 * @example
 * ```tsx
 * <div className="relative">
 *   <DataSourceBadge />
 *   <MyComponent />
 * </div>
 * ```
 */
export const DataSourceBadge: React.FC<DataSourceBadgeProps> = ({
  forceShow = false,
  className = ''
}) => {
  const { features } = useDevFeatures();

  if (!forceShow && !features.showDataSourceIndicator) {
    return null;
  }

  const isMock = features.useMockData;

  return (
    <div
      className={`
        inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-bold
        ${isMock
          ? 'bg-amber-500/20 text-amber-400 border border-amber-500/30'
          : 'bg-green-500/20 text-green-400 border border-green-500/30'
        }
        ${className}
      `}
      title={isMock
        ? 'Dati simulati (mock) - Console: devFeatures.useLive()'
        : 'Dati reali da API - Console: devFeatures.useMock()'
      }
    >
      <span className={`w-2 h-2 rounded-full ${isMock ? 'bg-amber-400' : 'bg-green-400'} animate-pulse`} />
      {isMock ? 'MOCK' : 'LIVE'}
    </div>
  );
};

export default {
  getDevFeatures,
  setDevFeature,
  resetDevFeature,
  resetAllDevFeatures,
  useMockData,
  showDataSourceIndicator,
  getMockDelay,
  simulateMockDelay,
  fetchWithMock,
  useDevFeatures,
  DataSourceBadge,
};
